<script>
import { mapGetters } from 'vuex';
import { debounce } from '@chatwoot/utils';
import MentionBox from '../mentions/MentionBox.vue';

export default {
  components: { MentionBox },
  props: {
    searchKey: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      // While a search is in flight (debounce + request) the list is hidden, so
      // the menu never shows the previous query's matches or the default full
      // list during the delay — that transient is what made typing "/boas"
      // quickly surface unrelated shortcuts. Starts true so a freshly opened
      // menu waits for its own fetch instead of flashing whatever the shared
      // store still held from an earlier search.
      isSearchPending: true,
    };
  },
  computed: {
    ...mapGetters({
      cannedMessages: 'getCannedResponses',
    }),
    items() {
      return this.cannedMessages.map(cannedMessage => ({
        label: cannedMessage.short_code,
        key: cannedMessage.short_code,
        description: cannedMessage.content,
        attachments: cannedMessage.files,
      }));
    },
  },
  watch: {
    searchKey() {
      this.isSearchPending = true;
      this.fetchCannedResponses();
    },
  },
  mounted() {
    this.fetchCannedResponses = debounce(async () => {
      if (this.isDestroyed) return;
      // Snapshot the term this fetch is for; only reveal the list when the
      // fetch that resolves still matches the current term. An older in-flight
      // request settling after newer keystrokes must not unhide the menu with
      // results that no longer match what was typed.
      const requestedSearchKey = this.searchKey;
      try {
        await this.$store.dispatch('getCannedResponse', {
          searchKey: requestedSearchKey,
        });
      } finally {
        if (!this.isDestroyed && this.searchKey === requestedSearchKey) {
          this.isSearchPending = false;
        }
      }
    }, 300);
    this.fetchCannedResponses();
  },
  beforeDestroy() {
    // The debounce helper has no cancel(); this flag stops a pending timer from
    // dispatching a fetch (and refilling the shared list) after the menu closed.
    this.isDestroyed = true;
  },
  methods: {
    handleMentionClick(item = {}) {
      this.$emit('click', item.description);
      this.$emit('insertAttachments', item.attachments);
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <MentionBox
    v-if="items.length && !isSearchPending"
    :items="items"
    @mentionSelect="handleMentionClick"
  />
</template>
